import '../../css/public/public.scss';
import instantsearch from 'instantsearch.js';
import 'instantsearch.css/themes/reset.css';

import { initTypesense } from '../shared/client';
import { initCart } from './modules/cart';
import { createAnalyticsMiddleware } from './modules/analytics';
import { setupRouting } from './modules/routing';
import { scrollToSnapfindAppTop } from './modules/facets';
import { getPromotionForQuery, renderPromotion } from './modules/promotions';
import { buildWidgets } from './modules/widgets';
import { setupSuggestionsUI } from './modules/suggestions';
import { initShortcuts } from './modules/shortcuts';
import { initStickySidebar } from './modules/sticky';

document.addEventListener('DOMContentLoaded', () => {
    // Initialize AJAX Add to Cart (delegated listener)
    initCart();
    const globalConfig = window.snapFindPublic;
    if (!globalConfig) return;

    // Initialize Keyboard Shortcuts
    initShortcuts();

    // Initialize Smart Sticky Sidebar
    initStickySidebar();

    const apps = document.querySelectorAll('.snapfind-app');
    const groupedApps = {};

    apps.forEach((app, idx) => {
        const pt = app.dataset.postType || 'product';
        const isSuggestionsGroup = app.classList.contains('type-suggestions');
        const groupKey = isSuggestionsGroup ? `suggestions:${pt}:${idx}` : `standard:${pt}`;

        if (!groupedApps[groupKey]) {
            groupedApps[groupKey] = {
                postType: pt,
                apps: [],
                isSuggestionsGroup,
            };
        }
        groupedApps[groupKey].apps.push(app);
    });

    const nonSuggestionsGroupCount = Object.values(groupedApps).filter(
        (group) => !group.isSuggestionsGroup
    ).length;

    const promotions = Array.isArray(globalConfig.frontend?.promotions)
        ? globalConfig.frontend.promotions
        : [];
    const isSearchPage = !!(globalConfig.context && globalConfig.context.is_search_page);
    const searchBaseUrl = String(globalConfig.context?.search_url || '/?s=');

    const bodyScrollLocks = new Set();
    const setBodyScrollLock = (key, isLocked) => {
        if (isLocked) {
            bodyScrollLocks.add(key);
        } else {
            bodyScrollLocks.delete(key);
        }

        const active = bodyScrollLocks.size > 0;
        document.body.style.overflow = active ? 'hidden' : '';

        // Only lock the suggestions dropdown if the mobile filter drawer is open.
        // We don't want to lock it when only the suggestions takeover itself is open.
        let lockDropdown = false;
        bodyScrollLocks.forEach((k) => {
            if (k.startsWith('mobile-drawer')) lockDropdown = true;
        });

        document.querySelectorAll('.snaf-suggestions-dropdown.is-open').forEach((el) => {
            el.style.overflow = lockDropdown ? 'hidden' : '';
        });
    };

    const buildSearchUrl = (query) =>
        `${searchBaseUrl}${encodeURIComponent(String(query || '').trim())}`;

    const shouldRenderPromotion = (app) =>
        app.classList.contains('type-suggestions') || isSearchPage;

    const dispatchSnapFindEvent = (eventName, detail) => {
        document.dispatchEvent(new CustomEvent(eventName, { detail }));
    };

    const runAfterNextPaint = (callback) => {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(callback);
        });
    };

    Object.values(groupedApps).forEach((group) => {
        const { postType, apps: appsInGroup, isSuggestionsGroup } = group;
        const config = globalConfig;
        if (!config) return;

        const typesenseAdapter = initTypesense(postType);
        if (!typesenseAdapter) return;

        const hasUpcField = (config.schema[postType] || []).some((f) => f.field_slug === 'upc');
        let activeUpcFilter = null;
        const indexName = `${config.server.collection_prefix}${postType}`;

        let previousUiState = null;
        let updatedEventFrame = null;
        let pendingUpdatedDetail = null;

        const getScrollTriggerState = (uiState) => {
            const indexState = uiState?.[indexName] ?? {};
            const { query, configure, ...triggerState } = indexState;
            return triggerState;
        };

        const scheduleUpdatedEvent = (detail) => {
            pendingUpdatedDetail = detail;
            if (updatedEventFrame !== null) return;

            updatedEventFrame = 1;
            runAfterNextPaint(() => {
                updatedEventFrame = null;
                if (!pendingUpdatedDetail) return;
                dispatchSnapFindEvent('snapfind:updated', pendingUpdatedDetail);
                pendingUpdatedDetail = null;
            });
        };

        const initialUrlQuery = new URLSearchParams(window.location.search).get('s') || '';

        const search = instantsearch({
            indexName,
            searchClient: typesenseAdapter.searchClient,
            initialUiState: {
                [indexName]: {
                    query: initialUrlQuery,
                },
            },
            onStateChange({ uiState, setUiState }) {
                const nextRefinements = JSON.stringify(getScrollTriggerState(uiState));
                const prevRefinements =
                    previousUiState === null
                        ? null
                        : JSON.stringify(getScrollTriggerState(previousUiState));

                if (prevRefinements !== null && prevRefinements !== nextRefinements) {
                    scrollToSnapfindAppTop(50, 950);
                }

                previousUiState = uiState;
                setUiState(uiState);
            },
            routing: isSuggestionsGroup
                ? nonSuggestionsGroupCount === 0
                    ? setupRouting(indexName)
                    : false
                : nonSuggestionsGroupCount === 1
                  ? setupRouting(indexName)
                  : false,
        });

        search.use(createAnalyticsMiddleware(postType));

        const SEARCH_DEBOUNCE_MS = 200;
        let searchDebounceTimer = null;
        const debounceQueryHook = (query, doSearch) => {
            if (hasUpcField) {
                const trimmed = String(query || '').trim();
                const isUpc = /^\d{12,13}$/.test(trimmed);
                const desiredFilter = isUpc ? `upc:=${trimmed}` : null;

                if (desiredFilter !== activeUpcFilter) {
                    activeUpcFilter = desiredFilter;
                    const base = typesenseAdapter._snapfindBaseConfig;
                    typesenseAdapter.updateConfiguration({
                        server: base.server,
                        additionalSearchParameters: desiredFilter
                            ? { ...base.additionalSearchParameters, filter_by: desiredFilter }
                            : { ...base.additionalSearchParameters },
                    });
                }
            }

            clearTimeout(searchDebounceTimer);
            searchDebounceTimer = setTimeout(() => doSearch(query), SEARCH_DEBOUNCE_MS);
        };

        const widgets = buildWidgets(
            postType,
            appsInGroup,
            config,
            isSuggestionsGroup,
            debounceQueryHook
        );

        // Setup Mobile Filter Drawer Logic (Sidebar)
        appsInGroup.forEach((app) => {
            const toggleBtn = app.querySelector('.snaf-mobile-filter-toggle');
            const closeBtn = app.querySelector('.snaf-mobile-filter-close');
            const overlay = app.querySelector('.snapfind-overlay');
            const sidebar = app.querySelector('.snapfind-sidebar');

            const toggleDrawer = () => {
                if (sidebar) sidebar.classList.toggle('is-open');
                if (overlay) overlay.classList.toggle('is-visible');
                const isOpen = !!(sidebar && sidebar.classList.contains('is-open'));
                setBodyScrollLock(`mobile-drawer:${postType}`, isOpen);
            };

            if (toggleBtn) toggleBtn.addEventListener('click', toggleDrawer);
            if (closeBtn) closeBtn.addEventListener('click', toggleDrawer);
            if (overlay) overlay.addEventListener('click', toggleDrawer);

            let resizeTimer;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(() => {
                    if (window.innerWidth > 860) {
                        if (sidebar && sidebar.classList.contains('is-open')) {
                            toggleDrawer();
                        }
                    }
                }, 100);
            });
        });

        if (hasUpcField && !isSuggestionsGroup) {
            const urlParams = new URLSearchParams(window.location.search);
            const initialQuery = urlParams.get('s') || '';
            const trimmed = String(initialQuery).trim();
            if (/^\d{12,13}$/.test(trimmed)) {
                activeUpcFilter = `upc:=${trimmed}`;
                const base = typesenseAdapter._snapfindBaseConfig;
                typesenseAdapter.updateConfiguration({
                    server: base.server,
                    additionalSearchParameters: {
                        ...base.additionalSearchParameters,
                        filter_by: activeUpcFilter,
                    },
                });
            }
        }

        search.addWidgets(widgets);
        search.start();

        runAfterNextPaint(() => {
            dispatchSnapFindEvent('snapfind:init', {
                postType,
                indexName,
                isSuggestionsGroup,
                appCount: appsInGroup.length,
                timestamp: Date.now(),
            });
        });

        search.on('render', () => {
            const currentQuery =
                search.helper && search.helper.state ? search.helper.state.query || '' : '';
            const promotion = getPromotionForQuery(currentQuery, promotions);

            appsInGroup.forEach((app) => {
                renderPromotion(app, shouldRenderPromotion(app) ? promotion : null);

                const btn = app.querySelector('.ais-InfiniteHits-loadMore');
                if (!btn) return;

                const renderState = search.renderState[indexName];
                const isLastPage =
                    renderState && renderState.infiniteHits
                        ? renderState.infiniteHits.isLastPage
                        : false;

                btn.innerText = isLastPage ? "That's all" : 'Load More';

                if (!btn.dataset.snafInited) {
                    btn.dataset.snafInited = 'true';
                    btn.addEventListener('click', () => {
                        if (!btn.classList.contains('ais-InfiniteHits-loadMore--disabled')) {
                            btn.innerText = 'Loading...';
                        }
                    });
                }
            });

            const helperState = search.helper && search.helper.state ? search.helper.state : null;
            const results =
                search.helper && search.helper.lastResults ? search.helper.lastResults : null;

            scheduleUpdatedEvent({
                postType,
                indexName,
                isSuggestionsGroup,
                appCount: appsInGroup.length,
                query: helperState && helperState.query ? helperState.query : '',
                page:
                    helperState && typeof helperState.page === 'number' ? helperState.page + 1 : 1,
                nbHits: results && typeof results.nbHits === 'number' ? results.nbHits : 0,
                timestamp: Date.now(),
            });
        });

        appsInGroup.forEach((app) => {
            setupSuggestionsUI(app, postType, bodyScrollLocks, setBodyScrollLock, buildSearchUrl);
        });
    });
});
